import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useState } from "react";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { getContracts } from "../../../../utils/urls";

import DisableContract from './disable_contract';
import TerminateContract from './terminate_contract';

const InactiveLeviesTab = () => {
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);

    // Activate flow
    const [selectedContractId, setSelectedContractId] = useState(null);
    const [confirmAction, setConfirmAction] = useState("");
    const [isConfirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [activateStartDate, setActivateStartDate] = useState(null);
    const [startDateDialogVisible, setStartDateDialogVisible] = useState(false);
    const [startDateError, setStartDateError] = useState("");

    // Terminate modal
    const [terminateDialogVisible, setTerminateDialogVisible] = useState(false);
    const [selectedTerminateContract, setSelectedTerminateContract] = useState(null);

    const fetchContracts = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) { toastify("No facility selected.", "error"); return; }

            const response = await makeRequest2(`${getContracts}/${facilityId}`, "GET");

            if (response?.success) {
                const contractsData = response.data?.contracts || response.data || [];

                // ── Inactive tab: Inactive OR Terminated only ───────────
                const inactiveOnly = contractsData.filter(c =>
                    c.status === 'Inactive' ||
                    c.status === 'Terminated'
                );

                setContracts(inactiveOnly);
                setFilteredContracts(inactiveOnly);
            } else {
                setContracts([]);
                setFilteredContracts([]);
            }
        } catch {
            toastify("Error fetching contracts", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContracts(); }, []);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setFilteredContracts(contracts.filter(c =>
            c.contractName?.toLowerCase().includes(term.toLowerCase()) ||
            c.customerName?.toLowerCase().includes(term.toLowerCase()) ||
            c.unitName?.toLowerCase().includes(term.toLowerCase())
        ));
    };

    // ── Activate flow: ask for start date first, then open confirm dialog ──
    const handleActivateClick = (rowData) => {
        setSelectedContractId(rowData._id);
        setActivateStartDate(null);
        setStartDateError("");
        setStartDateDialogVisible(true);
    };

    const handleStartDateContinue = () => {
        if (!activateStartDate) {
            setStartDateError("Please select a start date");
            return;
        }
        setStartDateDialogVisible(false);
        setConfirmAction("activate");
        setConfirmDialogVisible(true);
    };

    const handleStartDateCancel = () => {
        setStartDateDialogVisible(false);
        setActivateStartDate(null);
        setStartDateError("");
        setSelectedContractId(null);
    };

    // ── Templates ─────────────────────────────────────────────────────────────
    const dateTemplate = (rowData, field) => {
        if (!rowData[field]) return '-';
        try { return new Date(rowData[field]).toISOString().split("T")[0]; } catch { return '-'; }
    };

    const statusTemplate = (rowData) => {
        const terminated = rowData.status === 'Terminated';

        const styles = terminated
            ? { color: "#8b0000", bg: "#ffcccc", label: "Terminated" }
            : { color: "#ff4d4d", bg: "#ffebeb", label: "Inactive" };

        return (
            <span style={{ fontWeight: "bold", color: styles.color, padding: "5px 10px", borderRadius: "15px", backgroundColor: styles.bg }}>
                {styles.label}
            </span>
        );
    };

    const amountTemplate = (rowData) =>
        `${rowData.currencyCode || ''} ${rowData.amount?.toLocaleString() || '0'}`;

    const actionBodyTemplate = (rowData) => {
        const isTerminated = rowData.status === 'Terminated';

        return (
            <div className="actions d-flex flex-nowrap justify-content-center" style={{ gap: '0.25rem' }}>
                {/* Activate — only for Inactive, not Terminated */}
                {!isTerminated && (
                    <Button
                        icon="ti ti-lock-open"
                        className="p-button-rounded p-button-success p-button-sm"
                        onClick={() => handleActivateClick(rowData)}
                        tooltip="Activate"
                        style={{ width: '2.5rem', height: '2.5rem' }}
                    />
                )}
                {/* Terminate — only if not already terminated */}
                {!isTerminated && (
                    <Button
                        icon="ti ti-x"
                        className="p-button-rounded p-button-danger p-button-sm"
                        onClick={() => {
                            setSelectedTerminateContract(rowData);
                            setTerminateDialogVisible(true);
                        }}
                        tooltip="Terminate Contract"
                        style={{ width: '2.5rem', height: '2.5rem' }}
                    />
                )}
                {/* No buttons if already terminated */}
                {isTerminated && (
                    <span className="text-muted small fst-italic">No actions</span>
                )}
            </div>
        );
    };

    return (
        <div>
            {/* ── Search ── */}
            <div className="row mb-3">
                <div className="col-md-3">
                    <input
                        className="form-control"
                        placeholder="Search here"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            {/* ── Table ── */}
            <DataTable
                value={filteredContracts}
                emptyMessage="No inactive levy details found."
                paginator rows={10}
                stripedRows
                loading={loading}
                tableStyle={{ minWidth: "70rem" }}
                sortMode="multiple"
                removableSort
            >
                <Column field="contractName" header="Contract Name" sortable style={{ minWidth: '200px' }} />
                <Column field="customerName" header="Customer" body={(r) => r.customerName || '-'} sortable style={{ minWidth: '150px' }} />
                <Column field="unitName"     header="Unit"     body={(r) => r.unitName || '-'}     sortable style={{ minWidth: '120px' }} />
                <Column field="levyName"     header="Levy"     body={(r) => r.levyName || '-'}     sortable style={{ minWidth: '150px' }} />
                <Column field="amount"       header="Amount"   body={amountTemplate}               sortable style={{ minWidth: '120px' }} />
                <Column field="startDate"    header="Start Date" sortable body={(r) => dateTemplate(r, "startDate")} style={{ minWidth: '120px' }} />
                <Column field="status"       header="Status"   body={statusTemplate}               sortable style={{ minWidth: '120px' }} />
                <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '140px' }} />
            </DataTable>

            {/* ── Start Date picker (shown before activate confirmation) ── */}
            {startDateDialogVisible && (
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 1100
                    }}
                >
                    <div style={{ backgroundColor: '#fff', borderRadius: '6px', padding: '1.5rem', width: '350px' }}>
                        <h5 className="mb-3">Select Start Date</h5>
                        <p className="text-muted small mb-2">A start date is required to activate this contract.</p>
                        <Calendar
                            value={activateStartDate}
                            onChange={(e) => { setActivateStartDate(e.value); setStartDateError(""); }}
                            showIcon
                            dateFormat="mm/dd/yy"
                            className={startDateError ? 'p-invalid w-100' : 'w-100'}
                            style={{ width: '100%' }}
                        />
                        {startDateError && <small className="p-error d-block mt-1">{startDateError}</small>}
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary" onClick={handleStartDateCancel}>Cancel</button>
                            <button className="btn btn-success" onClick={handleStartDateContinue}>Continue</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modals ── */}
            {confirmAction === "activate" && (
                <DisableContract
                    visible={isConfirmDialogVisible}
                    onHide={() => setConfirmDialogVisible(false)}
                    contractId={selectedContractId}
                    isActivating={true}
                    startDate={activateStartDate}
                    onSuccess={fetchContracts}
                />
            )}
            <TerminateContract
                visible={terminateDialogVisible}
                onHide={() => setTerminateDialogVisible(false)}
                contractId={selectedTerminateContract?._id}
                contractName={selectedTerminateContract?.contractName}
                onSuccess={fetchContracts}
            />
        </div>
    );
};

export default InactiveLeviesTab;